/**
 * The four digest preference keys and their vocabulary (#1507 Story 1.2).
 *
 * THE SINGLE SOURCE for key names, allowed values, defaults and parsers. The
 * #1399 lesson (`.claude/rules/implementation-patterns.md`, "Normalization
 * parity"): a preference is read and written through the client service, the
 * route, the server service and the repository, and if any one of them
 * normalises differently the toggle "does not persist". All four layers
 * import this vocabulary; none re-states it.
 *
 * ⚠️ IT IS A MIRROR, NOT A SHARED MODULE. `server/tsconfig.json` sets
 * `rootDir: "./"`, so a server source file importing anything under `src/`
 * fails `yarn build:server` with TS6059 (measured on #1069, see
 * `server/services/internalIdentity.ts`). The client copy is
 * `src/config/digestPreferenceKeys.ts`, byte-for-byte the same body, and
 * `server/config/__tests__/digestPreferenceKeys.sync.test.ts` drives BOTH
 * copies through the one case table below. Edit both files together.
 *
 * Keys are house snake_case, like `email_notifications_enabled`, NOT the
 * dotted `notifications.*` names from the #1069 design (renamed 2026-09-30).
 *
 * @see docs/tech-design-1507-email-digest-service.md §7 API
 * @see GitLab #1507 — Email Digest Service
 */

import { AppRole } from '../types/auth';

export const DIGEST_PREFERENCE_KEYS = {
  /** Whether the person receives the Request changes email. */
  requestChanges: 'digest_request_changes_enabled',
  /** Whether the person receives the Discussion messages email. */
  correspondence: 'digest_correspondence_enabled',
  /** How often both emails may go out. One frequency for both kinds. */
  frequency: 'digest_frequency',
  /**
   * Whether requests the person can see only because of their role are
   * included. Shown to oversight roles only; stored for anyone who sets it.
   *
   * ⚠️ DEFAULTS TO `true`, AND THAT IS NOT A WIDENING. Before #1507 this
   * defaulted to `false` and did nothing at all, so everybody received their
   * full role-based set. `true` + the narrowing is byte-identical to that, and
   * turning it OFF is the new behaviour. The flat default is deliberate: for
   * the twelve roles whose visibility comes from a relationship the narrowing
   * is a no-op, so one global `true` IS "on for the global roles" without the
   * default needing to know anything about roles.
   */
  includeOversight: 'digest_include_oversight',
} as const;

export type DigestPreferenceKey =
  (typeof DIGEST_PREFERENCE_KEYS)[keyof typeof DIGEST_PREFERENCE_KEYS];

/** The three keys whose value is a JSON boolean. */
export const DIGEST_BOOLEAN_PREFERENCE_KEYS = [
  DIGEST_PREFERENCE_KEYS.requestChanges,
  DIGEST_PREFERENCE_KEYS.correspondence,
  DIGEST_PREFERENCE_KEYS.includeOversight,
] as const;

export type DigestBooleanPreferenceKey =
  (typeof DIGEST_BOOLEAN_PREFERENCE_KEYS)[number];

/**
 * The hourly cadences the picker offers.
 *
 * ⚠️ DIVISORS OF 24 ONLY, and that is why this list is four long rather than
 * twenty-four. A slot opens at `floor(hour / N) * N` from midnight UTC, so a
 * value that does not divide 24 leaves a SHORT last slot every single day:
 * "every 7 hours" is really 7, 7, 7, 3, and "every 23 hours" is 23 then 1.
 * Sixteen of the twenty-four do not mean what their own label says.
 *
 * The PRD asked for "exactly 1 through 24 hours, Daily and Weekly"; this
 * narrows it, which is a spec change made deliberately. It is free only
 * because the feature has never shipped — `FEATURE_NOTIFICATION_DIGEST` is off
 * in every environment, so there is no stored `h7` anywhere to orphan. Doing it
 * after release would need a migration.
 */
export const DIGEST_HOURLY_FREQUENCIES = ['h1', 'h4', 'h12', 'h24'] as const;

/**
 * A digest every minute. DEVELOPMENT ONLY — it is not in `DIGEST_FREQUENCIES`,
 * so every caller refuses it until one explicitly opts in.
 *
 * ⚠️ IT EXISTS SO THE CYCLE CAN BE WATCHED WORKING. At `h1` the shortest
 * honest wait between two digests is the top of the next hour, which makes a
 * manual test of the real scheduler an hour long. A minute is the same code
 * path on a cadence a person can sit through.
 *
 * ⚠️ THREE GATES, AND THE DEFAULT AT EACH IS "NO". `parseDigestFrequency`
 * refuses it unless the caller passes `allowMinute`; the write path asks
 * `isDigestMinuteFrequencyAllowed()` before storing it; and the digest pass
 * asks the same question before acting on a stored one, so a database copied
 * down from — or up to — an environment that does not allow it reads as the
 * default rather than mailing somebody 1,440 times a day.
 */
export const DIGEST_MINUTE_FREQUENCY = 'm1';

export const DIGEST_FREQUENCIES = [
  ...DIGEST_HOURLY_FREQUENCIES,
  'daily',
  'weekly',
] as readonly string[];

/** Everything storable, including the development-only cadence. */
export const DIGEST_FREQUENCIES_WITH_MINUTE = [
  DIGEST_MINUTE_FREQUENCY,
  ...DIGEST_FREQUENCIES,
] as readonly string[];

export type DigestHourlyFrequency = 'h1' | 'h4' | 'h12' | 'h24';

export type DigestFrequency = DigestHourlyFrequency | 'm1' | 'daily' | 'weekly';

/**
 * Defaults when no row is stored: both emails on, hourly, oversight ON.
 *
 * ⚠️ `includeOversight` IS `true`, AND THE COMMENT HERE SAID "off" UNTIL #1637.
 * It was `false` while the key widened a person's digest; it became `true` when
 * #1507 made the key NARROW instead, because `true` then means "do not narrow"
 * — which is byte-for-byte what every recipient already got. The flip changed
 * no behaviour, only which stored value means today's behaviour.
 *
 * ⚠️ `h1` IS HOURLY, AND IT APPLIES TO EVERY USER WHO HAS NEVER OPENED THE
 * PREFERENCE SCREEN. Worth knowing before `FEATURE_NOTIFICATION_DIGEST=on`
 * reaches an environment with real users on it.
 */
export const DIGEST_PREFERENCE_DEFAULTS = {
  [DIGEST_PREFERENCE_KEYS.requestChanges]: true,
  [DIGEST_PREFERENCE_KEYS.correspondence]: true,
  [DIGEST_PREFERENCE_KEYS.frequency]: 'h1' as DigestFrequency,
  [DIGEST_PREFERENCE_KEYS.includeOversight]: true,
} as const;

export const DEFAULT_DIGEST_FREQUENCY: DigestFrequency =
  DIGEST_PREFERENCE_DEFAULTS[DIGEST_PREFERENCE_KEYS.frequency];

/**
 * Who sees the `digest_include_oversight` control: the roles that may read a
 * request WITHOUT a team row. Mirrors `GLOBAL_CORRESPONDENCE_READER_ROLES`
 * (`server/services/internalIdentity.ts`), pinned by the sync test; the server
 * list stays the authority on what "oversight" means, this list only decides
 * whether the switch is rendered. The value is stored for anyone who sets it
 * and has no effect for a person with no oversight visibility.
 */
/**
 * Who sees the `digest_include_oversight` control.
 *
 * ⚠️ DELIBERATELY NO LONGER THE SAME LIST AS `GLOBAL_CORRESPONDENCE_READER_ROLES`
 * (#1507). The two answer different questions now: that one is "who may read
 * any request's correspondence", this one is "for whom does this switch change
 * anything". A supervisor's claim is scoped to her region and survives the
 * narrowing, so the switch is provably inert for her — and a control that does
 * nothing is worse than an absent one.
 */
export const DIGEST_OVERSIGHT_ROLES: readonly string[] = [
  AppRole.IT_ADMINISTRATOR,
  AppRole.CONFIG_ENGINEER,
  AppRole.BUSINESS_OWNER,
];

export function holdsDigestOversightRole(
  roles: readonly string[] | null | undefined,
): boolean {
  return (roles ?? []).some(role => DIGEST_OVERSIGHT_ROLES.includes(role));
}

export interface DigestPreferences {
  requestChangesEnabled: boolean;
  correspondenceEnabled: boolean;
  frequency: DigestFrequency;
  includeOversight: boolean;
}

export const DEFAULT_DIGEST_PREFERENCES: Readonly<DigestPreferences> = {
  requestChangesEnabled:
    DIGEST_PREFERENCE_DEFAULTS[DIGEST_PREFERENCE_KEYS.requestChanges],
  correspondenceEnabled:
    DIGEST_PREFERENCE_DEFAULTS[DIGEST_PREFERENCE_KEYS.correspondence],
  frequency: DEFAULT_DIGEST_FREQUENCY,
  includeOversight:
    DIGEST_PREFERENCE_DEFAULTS[DIGEST_PREFERENCE_KEYS.includeOversight],
};

export function isDigestPreferenceKey(
  key: unknown,
): key is DigestPreferenceKey {
  return (
    typeof key === 'string' &&
    (Object.values(DIGEST_PREFERENCE_KEYS) as readonly string[]).includes(key)
  );
}

export function isDigestBooleanPreferenceKey(
  key: unknown,
): key is DigestBooleanPreferenceKey {
  return (
    typeof key === 'string' &&
    (DIGEST_BOOLEAN_PREFERENCE_KEYS as readonly string[]).includes(key)
  );
}

/**
 * Exact match on the six tokens, else `null`. Never coerces: `'H4'`, `' h4'`,
 * `'4'`, `'hourly'`, `4` and `''` are all `null`, and a `null` at the route is
 * a 400. The same function reads the stored string back on the way out, where
 * `null` falls back to the default.
 *
 * ⚠️ `allowMinute` DEFAULTS TO FALSE, which is the point. `m1` is development
 * -only, and a gate whose default is "allowed" would be one forgotten import
 * away from being no gate at all. A caller that wants it must say so, and only
 * three do: the picker, the write path and the digest pass, each asking
 * `isDigestMinuteFrequencyAllowed()` first.
 */
export function parseDigestFrequency(
  raw: unknown,
  options: { allowMinute?: boolean } = {},
): DigestFrequency | null {
  const allowed = options.allowMinute
    ? DIGEST_FREQUENCIES_WITH_MINUTE
    : DIGEST_FREQUENCIES;
  return typeof raw === 'string' && allowed.includes(raw)
    ? (raw as DigestFrequency)
    : null;
}

/**
 * The WIRE form: a JSON boolean and nothing else. The strings `'true'` and
 * `'false'` are refused here on purpose; a client that sends them has lost
 * parity and must be told (400), not silently helped.
 */
export function parseDigestBoolean(raw: unknown): boolean | null {
  return typeof raw === 'boolean' ? raw : null;
}

/**
 * The STORED form: the repository keeps `'true'` / `'false'` strings (the
 * convention `retain_audio_ocr_files` and `email_notifications_enabled`
 * already use). Trim + lower-case to match
 * `getEnabledNotificationEmails`'s normalisation; anything else is `null`,
 * which the service turns into the key's default.
 */
export function parseStoredDigestBoolean(
  raw: string | null | undefined,
): boolean | null {
  if (typeof raw !== 'string') return null;
  const normalized = raw.trim().toLowerCase();
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;
  return null;
}

/** `'true'` / `'false'`: what the repository is handed for a boolean key. */
export function serializeDigestBoolean(value: boolean): 'true' | 'false' {
  return value ? 'true' : 'false';
}

/** "Every hour", "Every 4 hours", "Daily", "Weekly" — for the page and the email. */
export function digestFrequencyLabel(frequency: DigestFrequency): string {
  // Says "dev only" in the option itself: it is offered on a developer's own
  // machine beside five cadences that behave quite differently, and nothing
  // else on that row would tell them apart.
  if (frequency === DIGEST_MINUTE_FREQUENCY) return 'Every minute (dev only)';
  if (frequency === 'daily') return 'Daily';
  if (frequency === 'weekly') return 'Weekly';
  const hours = Number(frequency.slice(1));
  return hours === 1 ? 'Every hour' : `Every ${hours} hours`;
}

/**
 * The one table of good and bad values the four layers' tests import, so each
 * layer is held to the same literal answers rather than to each other.
 */
export const DIGEST_PREFERENCE_PARSE_CASES = {
  frequency: {
    accepted: ['h1', 'h4', 'h12', 'h24', 'daily', 'weekly'] as const,
    /**
     * Refused by every layer, opt-in or not — including the sixteen hourly
     * values that used to be offered and left a short slot each day.
     */
    refused: [
      'h2',
      'h7',
      'h23',
      'h0',
      'h25',
      'H4',
      ' h4',
      'h4 ',
      '4',
      'hourly',
      'Daily',
      '',
      4,
      true,
      null,
      undefined,
      {},
      [],
    ] as const,
    /**
     * Refused like the list above UNLESS the caller passes `allowMinute` — a
     * third state, because `m1` is neither universally valid nor universally
     * invalid. Kept out of `refused` so a layer that legitimately opts in (the
     * client service, which the server gates behind it) is not asserted to
     * reject it.
     */
    developmentOnly: ['m1'] as const,
  },
  boolean: {
    /** [wire value, expected] */
    accepted: [
      [true, true],
      [false, false],
    ] as const,
    refused: [
      'true',
      'false',
      'TRUE',
      1,
      0,
      '',
      null,
      undefined,
      {},
      [],
    ] as const,
  },
  storedBoolean: {
    /** [stored string, expected] */
    accepted: [
      ['true', true],
      ['false', false],
      ['TRUE', true],
      [' false ', false],
    ] as const,
    refused: ['', 'yes', '1', '0', 'null', null, undefined] as const,
  },
} as const;
